/**
 * TanStack Query Client Configuration
 * ====================================
 * Centralized query client for server state management.
 * Handles caching, background refresh, and request deduplication.
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: how long data is considered fresh (5 minutes)
      staleTime: 5 * 60 * 1000,

      // Cache time: how long data stays in cache (10 minutes)
      gcTime: 10 * 60 * 1000,

      // Retry failed requests up to 3 times
      retry: 3,

      // Refetch on window focus for fresh data
      refetchOnWindowFocus: true,

      // Don't refetch on reconnect by default
      refetchOnReconnect: false,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
});

// Query keys for type-safe query invalidation
export const queryKeys = {
  // Tasks
  tasks: {
    all: ['tasks'] as const,
    lists: () => [...queryKeys.tasks.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.tasks.lists(), filters] as const,
    details: () => [...queryKeys.tasks.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tasks.details(), id] as const,
  },

  // Claims
  claims: {
    all: ['claims'] as const,
    lists: () => [...queryKeys.claims.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.claims.lists(), filters] as const,
    details: () => [...queryKeys.claims.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.claims.details(), id] as const,
    search: (term: string) => [...queryKeys.claims.all, 'search', term] as const,
  },

  // Photo ID
  photoId: {
    all: ['photoId'] as const,
    task: (id: string) => [...queryKeys.photoId.all, 'task', id] as const,
    attachments: (taskId: string) => [...queryKeys.photoId.all, 'attachments', taskId] as const,
  },

  // Lenders
  lenders: {
    all: ['lenders'] as const,
    suggestions: () => [...queryKeys.lenders.all, 'suggestions'] as const,
    unknowns: () => [...queryKeys.lenders.all, 'unknowns'] as const,
  },
};
